import { Routes } from '@angular/router';
import { AuthGuard } from '@core/guards/auth.guard';
import { PermissionGuard } from '@core/guards/permission.guard';
import { environment } from '../environments/environment';

// Условные auth routes: KC mode → welcome page, Mock mode → auth forms
const authRoutes: Routes = environment.useKeycloakAuth
  ? [
      {
        path: 'welcome',
        loadComponent: () =>
          import('./features/welcome/welcome.component').then(m => m.WelcomeComponent)
      }
    ]
  : [
      {
        path: 'auth',
        loadChildren: () => import('./features/auth/auth.routes').then(m => m.authRoutes)
      }
    ];

export const routes: Routes = [
  // Root path: прямая загрузка Welcome (без redirect для устранения мелькания)
  {
    path: '',
    loadComponent: environment.useKeycloakAuth
      ? () => import('./features/welcome/welcome.component').then(m => m.WelcomeComponent)
      : () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
    pathMatch: 'full'
  },
  ...authRoutes,
  {
    path: 'calculator',
    loadChildren: () =>
      import('./features/calculator/calculator.routes').then(m => m.calculatorRoutes)
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./features/dashboard/dashboard.routes').then(m => m.dashboardRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'products',
    loadChildren: () => import('./features/products/products.routes').then(m => m.productsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'products/:id/prices',
    loadChildren: () =>
      import('./features/price-dashboard/price-dashboard.routes').then(m => m.priceDashboardRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'prices/loyalty',
    loadChildren: () =>
      import('./features/price-dashboard/pages/loyalty-prices/loyalty-prices.routes').then(
        m => m.loyaltyPricesRoutes
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'prices/sync-status',
    loadComponent: () =>
      import('./features/price-dashboard/pages/sync-status/sync-status.component').then(
        m => m.SyncStatusComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'prices-sync-status' }
  },
  {
    path: 'settings',
    loadChildren: () => import('./features/settings/settings.routes').then(m => m.settingsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'catalog/pim',
    loadComponent: () =>
      import('./features/catalog/pim-hub-page.component').then(m => m.PimHubPageComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-pim-hub' }
  },
  {
    path: 'catalog/unlinked',
    redirectTo: '/catalog-matching/unlinked',
    pathMatch: 'full'
  },
  {
    path: 'catalog/content-gen',
    loadComponent: () =>
      import('./features/catalog/content-generation-page.component').then(
        m => m.ContentGenerationPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-content-gen' }
  },
  {
    path: 'catalog/metrics',
    loadComponent: () =>
      import('./features/catalog/product-metrics-page.component').then(
        m => m.ProductMetricsPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-metrics' }
  },
  {
    path: 'catalog/pricing',
    loadComponent: () =>
      import('./features/catalog/pricing-dashboard-page.component').then(
        m => m.PricingDashboardPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-pricing' }
  },
  {
    path: 'catalog/categories/mapping',
    loadComponent: () =>
      import('./features/catalog/category-mapping-page.component').then(
        m => m.CategoryMappingPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-category-mapping' }
  },
  {
    path: 'catalog/categories',
    loadComponent: () =>
      import('./features/catalog/category-tree-page.component').then(
        m => m.CategoryTreePageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-categories' }
  },
  {
    path: 'catalog/duplicates',
    loadComponent: () =>
      import('./features/catalog/duplicates-page.component').then(m => m.DuplicatesPageComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-duplicates' }
  },
  {
    path: 'catalog/attributes',
    loadComponent: () =>
      import('./features/catalog/attributes-page.component').then(m => m.AttributesPageComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-attributes' }
  },
  {
    // Story 79.5 — PIM attribute groups management
    path: 'pim/attribute-groups',
    loadComponent: () =>
      import('./features/catalog/attribute-groups-page.component').then(
        m => m.AttributeGroupsPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'pim-attribute-groups' }
  },
  {
    // Story 81.2 — PIM product groups (collections)
    path: 'pim/product-groups',
    loadComponent: () =>
      import('./features/catalog/product-groups-page.component').then(
        m => m.ProductGroupsPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'pim-product-groups' }
  },
  {
    // Story 82.2 — PIM brands registry
    path: 'pim/brands',
    loadComponent: () =>
      import('./features/catalog/brands-page.component').then(m => m.BrandsPageComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'pim-brands' }
  },
  {
    // Story 83.1 — PIM marketplace attribute mapping
    path: 'pim/mp-attribute-mapping',
    loadComponent: () =>
      import('./features/catalog/mp-attribute-mapping-page.component').then(
        m => m.MpAttributeMappingPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'pim-mp-attribute-mapping' }
  },
  {
    // Story 84.3 — XLSX import wizard
    path: 'catalog/import',
    loadComponent: () =>
      import('./features/catalog/import-wizard-page.component').then(
        m => m.ImportWizardPageComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-import' }
  },
  {
    path: 'catalog/quality',
    loadComponent: () =>
      import('./features/catalog/quality-dashboard.component').then(
        m => m.QualityDashboardComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'catalog-quality' }
  },
  {
    path: 'catalog/card-audit',
    loadChildren: () =>
      import('./features/card-audit/card-audit.routes').then(m => m.cardAuditRoutes),
    canActivate: [AuthGuard]
  },
  {
    // Story 113.6: specific catalog route before generic lazy catalog/:id.
    path: 'catalog/grey-market',
    loadComponent: () =>
      import('./features/grey-market/grey-market.component').then(m => m.GreyMarketComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'grey-market' }
  },
  {
    // Специфичный маршрут должен стоять ПЕРЕД generic 'catalog' —
    // тот регистрирует loadChildren с catalog-detail ':id' catch-all, который
    // иначе перехватывает 'niche-analytics' как ID товара (тот же паттерн,
    // что уже применён выше для catalog/quality и catalog/card-audit).
    path: 'catalog/niche-analytics',
    loadComponent: () =>
      import('./features/niche-analytics/niche-analytics.component').then(
        m => m.NicheAnalyticsComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'niche-analytics' }
  },
  {
    path: 'catalog',
    loadChildren: () => import('./features/catalog/catalog.routes').then(m => m.catalogRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'orders',
    loadChildren: () => import('./features/orders/orders.routes').then(m => m.ordersRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'analytics',
    loadChildren: () =>
      import('./features/analytics/analytics.routes').then(m => m.analyticsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'pricing-rules',
    loadChildren: () =>
      import('./features/pricing-rules/pricing-rules.routes').then(m => m.pricingRulesRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'competitors',
    loadChildren: () =>
      import('./features/competitors/competitors.routes').then(m => m.competitorsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'repricing',
    loadChildren: () =>
      import('./features/repricing/repricing.routes').then(m => m.repricingRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'catalog-matching',
    loadChildren: () =>
      import('./features/catalog-matching/catalog-matching.routes').then(
        m => m.CATALOG_MATCHING_ROUTES
      ),
    canActivate: [AuthGuard]
  },
  {
    path: 'cost-management',
    loadComponent: () =>
      import('./features/cost-management/cost-management.component').then(
        m => m.CostManagementComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'cost-management' }
  },
  {
    path: 'cost-management/bulk-import',
    loadComponent: () =>
      import('./features/cost-management/bulk-cost-import.component').then(
        m => m.BulkCostImportComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'cost-management-bulk-import' }
  },
  {
    path: 'products/import-erp',
    loadComponent: () =>
      import('./features/products/erp-import-wizard.component').then(
        m => m.ErpImportWizardComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'erp-import' }
  },
  {
    path: 'warehouses',
    loadChildren: () =>
      import('./features/warehouses/warehouses.routes').then(m => m.warehousesRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'price-comparison',
    loadComponent: () =>
      import('./features/price-comparison/price-comparison.component').then(
        m => m.PriceComparisonComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'price-comparison' }
  },
  {
    path: 'discounts',
    loadComponent: () =>
      import('./features/discounts/discounts.component').then(m => m.DiscountsComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'discounts' }
  },
  {
    path: 'promotions',
    loadComponent: () =>
      import('./features/promotions/promotions.component').then(m => m.PromotionsComponent),
    canActivate: [AuthGuard],
    data: { helpTopic: 'promotions' }
  },
  {
    path: 'costs',
    loadChildren: () => import('./features/costs/costs.routes').then(m => m.costsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'organizational-finance',
    loadChildren: () =>
      import('./features/organizational-finance/organizational-finance.routes').then(
        m => m.organizationalFinanceRoutes
      ),
    canActivate: [AuthGuard, PermissionGuard('org_finance.view')]
  },
  {
    path: 'stocks',
    loadChildren: () => import('./features/stocks/stocks.routes').then(m => m.stocksRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'promotions',
    loadChildren: () =>
      import('./features/promotions/promotions.routes').then(m => m.promotionsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'reviews',
    loadChildren: () => import('./features/reviews/reviews.routes').then(m => m.reviewsRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'feedback',
    loadChildren: () => import('./features/feedback/feedback.routes').then(m => m.feedbackRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'admin/incidents',
    loadComponent: () =>
      import('./features/feedback/admin-dashboard/admin-dashboard.component').then(
        m => m.AdminDashboardComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'admin-incidents' }
  },
  {
    path: 'admin/webhook-events',
    loadComponent: () =>
      import('./features/admin/webhook-events/webhook-events.component').then(
        m => m.WebhookEventsComponent
      ),
    canActivate: [AuthGuard],
    data: { helpTopic: 'admin-webhook-events' }
  },
  {
    path: 'help',
    loadChildren: () => import('./features/help/help.routes').then(m => m.helpRoutes),
    canActivate: [AuthGuard]
  },
  {
    path: 'exchange-log',
    loadChildren: () =>
      import('./features/exchange-log/exchange-log.routes').then(m => m.exchangeLogRoutes),
    canActivate: [AuthGuard]
  },
  // Публичная страница регистрации по приглашению (без AuthGuard — доступна всем)
  {
    path: 'register',
    loadComponent: () =>
      import('./features/auth/invite-register/invite-register.component').then(
        m => m.InviteRegisterComponent
      )
  },
  {
    path: '**',
    redirectTo: '/dashboard'
  }
];
